import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { getNews, createNews, updateNews, deleteNews, uploadImage, uploadMedia, getCategories, getFileUrl, convertYouTubeUrl } from '../../services/apiService';
import RichTextEditor from '../RichTextEditor';
import { stripHtml, getExcerpt } from '../../utils/htmlUtils';

const News = () => {
  const [userEmail, setUserEmail] = useState('');
  const [news, setNews] = useState([]);
  const [editingNews, setEditingNews] = useState(null);
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date', 'title', 'views', 'status'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [newsToDelete, setNewsToDelete] = useState(null); // Pour la popup de suppression
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null); // Pour la notification toast
  const [availableCategories, setAvailableCategories] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const email = localStorage.getItem('userEmail');
    
    if (!isLoggedIn || !email) {
      navigate('/login');
      return;
    }
    
    setUserEmail(email);
    fetchNews();
    fetchCategories();
  }, [navigate]);

  const fetchCategories = async () => {
    try {
      const categories = await getCategories();
      setAvailableCategories((categories || [])
        .map((c) => ({ id: c.id, name: c.name }))
        .filter((c) => c.id && c.name));
    } catch (e) {
      // fallback: keep empty list; admin can create categories in /admin/categories
      setAvailableCategories([]);
    }
  };

  const fetchNews = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const newsData = await getNews();
      // Transformer les données de l'API pour correspondre au format attendu
      const transformedNews = newsData.map(item => ({
        id: item.id,
        title: item.title,
        content: item.content || '',
        image: item.image_url || (item.image_file_path ? getFileUrl(item.image_file_path) : (item.media && item.media.length > 0 && item.media[0].media_type === 'image' ? (item.media[0].url ? getFileUrl(item.media[0].url) : getFileUrl(item.media[0].file_path)) : '')),
        imageType: item.image_url ? 'link' : 'upload',
        date: item.created_at ? item.created_at.split('T')[0] : new Date().toISOString().split('T')[0],
        status: item.status || 'Brouillon',
        views: item.views || 0,
        author: item.author_name || item.author || 'Admin',
        categories: item.categories || [],
        category_ids: item.category_ids || [],
        media: (item.media || []).map(m => ({
          id: m.id,
          type: m.media_type === 'image' ? 'image' : 'video',
          url: m.url ? getFileUrl(m.url) : (m.file_path ? getFileUrl(m.file_path) : ''),
          file_path: m.file_path || null, // Préserver le file_path original
          uploadType: m.url ? 'link' : 'upload',
          fileName: m.file_name || (m.file_path ? m.file_path.split('/').pop() : undefined),
          file_size: m.file_size || null,
          mime_type: m.mime_type || null
        }))
      }));
      setNews(transformedNews);
    } catch (err) {
      setError('Error loading news');
      setNews([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour générer un nom de fichier unique
  const generateFileName = (originalFileName, type) => {
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 9);
    const extension = originalFileName.split('.').pop();
    const prefix = type === 'image' ? 'img' : 'vid';
    return `${prefix}_${timestamp}_${randomId}.${extension}`;
  };

  // Fonction pour obtenir le chemin relatif du fichier
  const getFileRelativePath = (fileName, type) => {
    return `/aseets/images/Admin/News/${fileName}`;
  };

  // Fonction pour copier le fichier vers le dossier (nécessite un backend)
  const saveFileToServer = async (file, fileName, type) => {
    // Pour l'instant, on retourne le chemin relatif
    // Dans un vrai projet, vous devriez envoyer le fichier au backend via FormData
    // Exemple avec fetch:
    /*
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', fileName);
    formData.append('type', type);
    formData.append('folder', 'Admin/News');
    
    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      return data.filePath; // Retourne le chemin du fichier sauvegardé
    } catch (error) {
      throw error;
    }
    */
    
    // Pour le développement, on retourne juste le chemin relatif
    return getFileRelativePath(fileName, type);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 4000); // Disparaît après 4 secondes
  };

  const handleNewsSubmit = async (newsData) => {
    try {
      let imageUrl = null;
      let imageFilePath = null;
      let imageSourceType = 'url';

      // Uploader l'image principale si c'est un upload
      if (newsData.imageType === 'upload' && newsData.uploadedFile) {
        try {
          const uploadedFile = await uploadImage(newsData.uploadedFile);
          if (uploadedFile) {
            imageFilePath = uploadedFile.path;
            imageSourceType = 'upload';
          }
        } catch (error) {
          showNotification('Error uploading image: ' + (error.message || 'Unknown error'), 'error');
          return;
        }
      } else if (newsData.imageType === 'link') {
        imageUrl = newsData.image || null;
      }

      // Uploader les médias
      const uploadedMedia = [];
      if (newsData.media && newsData.media.length > 0) {
        const filesToUpload = newsData.media
          .filter(mediaItem => mediaItem.uploadType === 'upload' && mediaItem.file)
          .map(mediaItem => mediaItem.file);

        if (filesToUpload.length > 0) {
          try {
            const uploadedFiles = await uploadMedia(filesToUpload);
            let uploadedIndex = 0;
            
            for (const mediaItem of newsData.media) {
              if (mediaItem.uploadType === 'upload' && mediaItem.file) {
                // Nouveau fichier à uploader
                const uploadedFile = uploadedFiles[uploadedIndex];
                uploadedMedia.push({
                  media_type: mediaItem.type,
                  file_path: uploadedFile.path,
                  source_type: 'upload',
                  file_name: uploadedFile.filename,
                  file_size: uploadedFile.size,
                  mime_type: uploadedFile.mimetype
                });
                uploadedIndex++;
              } else if (mediaItem.uploadType === 'upload' && mediaItem.file_path) {
                // Média existant uploadé (préserver le file_path existant)
                uploadedMedia.push({
                  media_type: mediaItem.type,
                  file_path: mediaItem.file_path,
                  source_type: 'upload',
                  file_name: mediaItem.fileName || mediaItem.file_path.split('/').pop(),
                  file_size: mediaItem.file_size || null,
                  mime_type: mediaItem.mime_type || null
                });
              } else if (mediaItem.uploadType === 'link' && mediaItem.url) {
                // Média avec URL
                uploadedMedia.push({
                  media_type: mediaItem.type,
                  url: mediaItem.url,
                  source_type: 'url'
                });
              }
            }
          } catch (error) {
            showNotification('Error uploading media: ' + (error.message || 'Unknown error'), 'error');
            return;
          }
        } else {
          // Si pas de fichiers à uploader, préserver les médias existants
          for (const mediaItem of newsData.media) {
            if (mediaItem.uploadType === 'upload' && mediaItem.file_path) {
              // Média existant uploadé
              uploadedMedia.push({
                media_type: mediaItem.type,
                file_path: mediaItem.file_path,
                source_type: 'upload',
                file_name: mediaItem.fileName || mediaItem.file_path.split('/').pop(),
                file_size: mediaItem.file_size || null,
                mime_type: mediaItem.mime_type || null
              });
            } else if (mediaItem.uploadType === 'link' && mediaItem.url) {
              // Média avec URL
              uploadedMedia.push({
                media_type: mediaItem.type,
                url: mediaItem.url,
                source_type: 'url'
              });
            }
          }
        }
      }

      // Préparer les données pour l'API
      const apiData = {
        title: newsData.title.trim(),
        content: newsData.content ? newsData.content.trim() : '',
        image_url: imageUrl,
        image_file_path: imageFilePath,
        image_source_type: imageSourceType,
        status: newsData.status || 'Brouillon',
        author_id: 1, // TODO: Récupérer l'ID de l'utilisateur connecté
        categories: newsData.categories || [],
        media: uploadedMedia
      };
      
      let savedNews;
      if (editingNews) {
        // Mettre à jour
        savedNews = await updateNews(editingNews.id, apiData);
        if (savedNews) {
          // Recharger les news depuis l'API
          await fetchNews();
          setShowNewsForm(false);
          setEditingNews(null);
          showNotification('News updated successfully', 'success');
        } else {
          showNotification('Error updating news', 'error');
        }
      } else {
        // Créer
        savedNews = await createNews(apiData);
        if (savedNews) {
          // Recharger les news depuis l'API
          await fetchNews();
          setShowNewsForm(false);
          setEditingNews(null);
          showNotification('News created successfully', 'success');
        } else {
          showNotification('Error creating news', 'error');
        }
      }
    } catch (error) {
      showNotification('Error saving: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const handleDeleteNews = (id) => {
    const item = news.find(n => n.id === id);
    if (item) {
      setNewsToDelete(item);
    }
  };

  const confirmDeleteNews = async () => {
    if (newsToDelete) {
      try {
        const success = await deleteNews(newsToDelete.id);
        if (success) {
          // Recharger les news depuis l'API
          await fetchNews();
          setNewsToDelete(null);
          showNotification('News deleted successfully', 'success');
        } else {
          showNotification('Error deleting news', 'error');
        }
      } catch (error) {
        showNotification('Error deleting: ' + (error.message || 'Unknown error'), 'error');
      }
    }
  };

  const handleEditNews = (item) => {
    setEditingNews(item);
    setShowNewsForm(true);
  };

  // Filtrage des news
  const filteredNews = news.filter(item => {
    // Filtre par recherche (titre ou contenu)
    const matchesSearch = searchTerm === '' || 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.content && item.content.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Filtre par date
    const matchesDate = dateFilter === '' || item.date === dateFilter;
    
    // Filtre par catégorie
    const matchesCategory = categoryFilter === '' || 
      (item.categories && Array.isArray(item.categories) && item.categories.includes(categoryFilter));

    // Filter by status (keep stored values like "Publié/Brouillon")
    const matchesStatus = statusFilter === '' || item.status === statusFilter;
    
    return matchesSearch && matchesDate && matchesCategory && matchesStatus;
  });

  // Tri des news filtrées
  const sortedNews = [...filteredNews].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'date':
        comparison = new Date(a.date) - new Date(b.date);
        break;
      case 'title':
        comparison = a.title.localeCompare(b.title, 'fr', { sensitivity: 'base' });
        break;
      case 'views':
        comparison = (a.views || 0) - (b.views || 0);
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status, 'fr', { sensitivity: 'base' });
        break;
      default:
        comparison = 0;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Réinitialiser la page à 1 lors du changement de filtre ou tri
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter, categoryFilter, statusFilter, sortBy, sortOrder]);

  const getStatusLabel = (status) => {
    if (status === 'Publié') return 'Published';
    if (status === 'Brouillon') return 'Draft';
    return status || '';
  };

  const allStatuses = Array.from(new Set(news.map(n => n.status).filter(Boolean))).sort();

  // Pagination basée sur les news filtrées et triées
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentNews = sortedNews.slice(startIndex, endIndex);
  const totalPages = Math.ceil(sortedNews.length / itemsPerPage);

  // Obtenir toutes les catégories uniques
  const allCategories = Array.from(new Set(
    news.flatMap(item => item.categories || [])
  )).sort();

  // Afficher le chargement
  if (isLoading) {
    return (
      <AdminLayout>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '24px', color: '#96ca2d', marginBottom: '20px' }}>
            <i className="fas fa-spinner fa-spin" />
          </div>
          <p style={{ fontSize: '16px', color: '#666' }}>Loading news...</p>
        </div>
      </AdminLayout>
    );
  }

  // Afficher l'erreur
  if (error) {
    return (
      <AdminLayout>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '24px', color: '#ef4444', marginBottom: '20px' }}>
            <i className="fas fa-exclamation-triangle" />
          </div>
          <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px' }}>{error}</p>
          <button
            onClick={fetchNews}
            style={{
              padding: '10px 20px',
              background: '#96ca2d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600
            }}
          >
            Réessayer
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Notification Toast */}
      {notification && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 10001,
            minWidth: '300px',
            maxWidth: '500px',
            background: notification.type === 'success' ? '#10b981' : '#ef4444',
            color: 'white',
            padding: '16px 20px',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'slideInRight 0.3s ease-out',
            cursor: 'pointer'
          }}
          onClick={() => setNotification(null)}
        >
          <style>{`
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
            @keyframes slideOutRight {
              from {
                transform: translateX(0);
                opacity: 1;
              }
              to {
                transform: translateX(100%);
                opacity: 0;
              }
            }
          `}</style>
          <div style={{
            fontSize: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '24px'
          }}>
            {notification.type === 'success' ? (
              <i className="fas fa-check-circle" />
            ) : (
              <i className="fas fa-exclamation-circle" />
            )}
          </div>
          <div style={{ flex: 1 }}>
            <p style={{
              margin: 0,
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '1.5'
            }}>
              {notification.message}
            </p>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setNotification(null);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              opacity: 0.8,
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.8'}
          >
            <i className="fas fa-times" />
          </button>
        </div>
      )}
      
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: '200px',
          height: '200px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 2, flexWrap: 'wrap', gap: '20px' }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 700 }}>News Management</h1>
            <p style={{ margin: '8px 0 0 0', opacity: 0.95 }}>Manage your news and articles</p>
            {/* Statistiques */}
            <div style={{ 
              display: 'flex', 
              gap: '16px', 
              marginTop: '24px',
              flexWrap: 'wrap'
            }}>
              {/* Total news */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: '12px',
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                padding: '12px 20px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  color: 'white'
                }}>
          <i className="fas fa-newspaper" />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: 'white', lineHeight: '1.2' }}>
                    {news.length}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', fontWeight: 500, marginTop: '2px' }}>
                    Total news
                  </div>
                </div>
              </div>

              {/* Publiés */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: '12px',
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                padding: '12px 20px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  color: 'white'
                }}>
                  <i className="fas fa-check-circle" />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: 'white', lineHeight: '1.2' }}>
                    {news.filter(n => n.status === 'Publié').length}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', fontWeight: 500, marginTop: '2px' }}>
                    Published
                  </div>
                </div>
      </div>

              {/* Vues totales */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: '12px',
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                padding: '12px 20px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  color: 'white'
                }}>
                  <i className="fas fa-eye" />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: 'white', lineHeight: '1.2' }}>
                    {news.reduce((sum, n) => sum + (n.views || 0), 0)}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', fontWeight: 500, marginTop: '2px' }}>
                    Total Views
                  </div>
                </div>
              </div>
            </div>
          </div>
        <button
          onClick={() => {
            setEditingNews(null);
            setShowNewsForm(true);
          }}
          style={{
            padding: '12px 24px',
              background: 'rgba(255,255,255,0.2)',
              border: '2px solid white',
              borderRadius: '8px',
            color: 'white',
              cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 600,
              transition: 'all 0.3s ease',
              alignSelf: 'flex-start'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.color = '#96ca2d';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
              e.currentTarget.style.color = 'white';
            }}
          >
            + New News
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '24px',
        border: '1px solid rgba(150, 202, 45, 0.1)',
        display: 'flex',
        gap: '16px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Recherche */}
        <div style={{ flex: '1', minWidth: '250px' }}>
          <div style={{ position: 'relative' }}>
            <i className="fas fa-search" style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '14px'
            }} />
            <input
              type="text"
              placeholder="Search by title or content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#96ca2d';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
              }}
            />
          </div>
        </div>

        {/* Filtre par date */}
        <div style={{ minWidth: '180px' }}>
          <div style={{ position: 'relative' }}>
            <i className="fas fa-calendar-alt" style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '14px'
            }} />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#96ca2d';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
              }}
            />
          </div>
        </div>

        {/* Filtre par catégorie */}
        <div style={{ minWidth: '200px' }}>
          <div style={{ position: 'relative' }}>
            <i className="fas fa-tags" style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '14px',
              zIndex: 1
            }} />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                backgroundColor: 'white',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                paddingRight: '36px'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#96ca2d';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
              }}
            >
              <option value="">All Categories</option>
              {allCategories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Status filter */}
        <div style={{ minWidth: '180px' }}>
          <div style={{ position: 'relative' }}>
            <i className="fas fa-filter" style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '14px',
              zIndex: 1
            }} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 36px 10px 38px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                boxShadow: 'none'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#96ca2d';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <option value="">All statuses</option>
              <option value="Publié">Published</option>
              <option value="Brouillon">Draft</option>
              {allStatuses
                .filter((status) => status !== 'Publié' && status !== 'Brouillon')
                .map(status => (
                  <option key={status} value={status}>{getStatusLabel(status)}</option>
                ))}
            </select>
          </div>
        </div>

        {/* Tri */}
        <div style={{ minWidth: '180px' }}>
          <div style={{ position: 'relative' }}>
            <i className="fas fa-sort" style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '14px',
              zIndex: 1
            }} />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                backgroundColor: 'white',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                paddingRight: '36px'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#96ca2d';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
              }}
            >
              <option value="date">By Date</option>
              <option value="title">By Title</option>
              <option value="views">By Views</option>
              <option value="status">By Status</option>
            </select>
          </div>
        </div>

        {/* Ordre de tri */}
        <div style={{ minWidth: '140px' }}>
          <div style={{ position: 'relative' }}>
            <i className={`fas fa-sort-${sortOrder === 'asc' ? 'amount-up' : 'amount-down'}`} style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '14px',
              zIndex: 1
            }} />
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                backgroundColor: 'white',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                paddingRight: '36px'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#96ca2d';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
              }}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>
        </div>

        {/* Bouton réinitialiser */}
        {(searchTerm || dateFilter || categoryFilter || statusFilter) && (
          <button
            onClick={() => {
              setSearchTerm('');
              setDateFilter('');
              setCategoryFilter('');
              setStatusFilter('');
            }}
            style={{
              padding: '10px 20px',
              background: '#f3f4f6',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              color: '#6b7280',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#e5e7eb';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f3f4f6';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}
          >
            <i className="fas fa-times" style={{ fontSize: '12px' }} />
            Reset
        </button>
        )}
      </div>

      {/* News List */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        border: '1px solid rgba(150, 202, 45, 0.1)'
      }}>
        {sortedNews.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            <p>
              {(searchTerm || dateFilter || categoryFilter) 
                ? 'No news match the selected filters.' 
                : 'No news at the moment.'}
            </p>
            {(searchTerm || dateFilter || categoryFilter) ? (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setDateFilter('');
                  setCategoryFilter('');
                }}
                style={{
                  padding: '12px 24px',
                  background: '#96ca2d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 600,
                  marginTop: '16px'
                }}
              >
                Reset Filters
              </button>
            ) : (
              <button
                onClick={() => {
                  setEditingNews(null);
                  setShowNewsForm(true);
                }}
                style={{
                  padding: '12px 24px',
                  background: '#96ca2d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 600,
                  marginTop: '16px'
                }}
              >
                Create First News
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Indicateur de résultats */}
            {(searchTerm || dateFilter || categoryFilter) && (
              <div style={{
                marginBottom: '20px',
                padding: '12px 16px',
                background: '#f0fdf4',
                border: '1px solid #86efac',
                borderRadius: '8px',
                color: '#166534',
                fontSize: '14px',
              display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <i className="fas fa-info-circle" style={{ fontSize: '16px' }} />
                <span>
                  {sortedNews.length} {sortedNews.length === 1 ? 'result found' : 'results found'}
                  {(searchTerm || dateFilter || categoryFilter) && ' with filters applied'}
                </span>
              </div>
            )}
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: window.innerWidth <= 768 ? '16px' : '20px'
            }}>
              {currentNews.map(item => (
                <div key={item.id} style={{
                  background: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#96ca2d';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(150, 202, 45, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '0px',
                    left: '0px',
                    right: '0px',
                    height: '3px',
                    background: 'linear-gradient(135deg, rgb(150, 202, 45) 0%, rgb(127, 184, 0) 100%)',
                    transform: 'translateY(0px)',
                    borderColor: 'rgba(150, 202, 45, 0.1)',
                    zIndex: 2
                  }} />
                  <div style={{ position: 'relative', width: '100%', height: '180px' }}>
                    {item.image ? (
                      <img 
                        src={item.image} 
                        alt={item.title}
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover',
                          display: 'block'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const placeholder = e.target.parentElement.querySelector('.image-placeholder');
                          if (placeholder) {
                            placeholder.style.display = 'flex';
                          }
                        }}
                      />
                    ) : (
                      <div 
                        className="image-placeholder"
                        style={{
                          display: 'flex',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '48px'
                        }}
                      >
                        <i className="fas fa-newspaper" />
                      </div>
                    )}
                    
                    {/* Catégories sur l'image */}
                    {item.categories && Array.isArray(item.categories) && item.categories.length > 0 && (
                      <div style={{ 
                        position: 'absolute',
                        top: '12px',
                        left: '12px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px',
                        zIndex: 3
                      }}>
                        {item.categories.slice(0, 3).map((category, idx) => (
                          <span
                            key={idx}
                            style={{
                              display: 'inline-block',
                              padding: '4px 10px',
                              background: 'rgba(150, 202, 45, 0.95)',
                              color: '#ffffff',
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              borderRadius: '10px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              backdropFilter: 'blur(4px)',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {category}
                          </span>
                        ))}
                        {item.categories.length > 3 && (
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '4px 10px',
                              background: 'rgba(0, 0, 0, 0.6)',
                              color: '#ffffff',
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              borderRadius: '10px',
                              backdropFilter: 'blur(4px)',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                            }}
                          >
                            +{item.categories.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ 
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: '200px'
                  }}>
                    {/* Titre - hauteur fixe */}
                    <div style={{ height: '44px', marginBottom: '16px', display: 'flex', alignItems: 'flex-start' }}>
                      <h3 style={{ 
                        margin: 0, 
                        fontSize: '18px', 
                        fontWeight: 600, 
                        color: '#1f2937',
                        lineHeight: '1.4',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>{item.title}</h3>
                    </div>
                    
                    {/* Contenu - hauteur fixe */}
                    <div style={{ height: '40px', marginBottom: '16px' }}>
                      <p style={{ 
                        margin: 0, 
                        color: '#6b7280', 
                        fontSize: '14px', 
                        lineHeight: '1.5', 
                        display: '-webkit-box', 
                        WebkitLineClamp: 2, 
                        WebkitBoxOrient: 'vertical', 
                        overflow: 'hidden' 
                      }}>{item.content ? getExcerpt(item.content, 100) : ''}</p>
                    </div>
                    
                    {/* Métadonnées - hauteur fixe */}
                    <div style={{ 
                      height: '26px', 
                      display: 'flex', 
                      gap: '12px', 
                      fontSize: '12px', 
                      color: '#9ca3af', 
                      marginBottom: '30px',
                      alignItems: 'center',
                      flexWrap: 'wrap'
                    }}>
                  <span><i className="fas fa-eye" style={{ marginRight: '4px' }} />{item.views} views</span>
                  <span><i className="fas fa-calendar" style={{ marginRight: '4px' }} />{item.date}</span>
                  <span style={{
                    background: item.status === 'Publié' ? '#dcfce7' : '#fef3c7',
                    color: item.status === 'Publié' ? '#166534' : '#92400e',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontWeight: 600
                  }}>{item.status === 'Publié' ? 'Published' : item.status === 'Brouillon' ? 'Draft' : item.status}</span>
                </div>
                    
                    {/* Boutons - Design moderne */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '10px', 
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      marginTop: '12px'
                    }}>
                <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/comments?article=${encodeURIComponent(item.title)}&type=news`);
                        }}
                  style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '6px',
                          width: '32px',
                          height: '32px',
                          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: '0 2px 6px rgba(59, 130, 246, 0.25)',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 6px rgba(59, 130, 246, 0.25)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                        }}
                        title="Comments"
                      >
                        <i className="fas fa-comments" style={{ fontSize: '14px' }} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditNews(item);
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '6px',
                          width: '32px',
                          height: '32px',
                          background: 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: '0 2px 6px rgba(150, 202, 45, 0.3)',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(150, 202, 45, 0.45)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #7ba818 0%, #6a9815 100%)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 6px rgba(150, 202, 45, 0.3)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)';
                        }}
                        title="Edit"
                      >
                        <i className="fas fa-edit" style={{ fontSize: '14px' }} />
                </button>
                <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNews(item.id);
                        }}
                  style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '6px',
                          width: '32px',
                          height: '32px',
                          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: '0 2px 6px rgba(239, 68, 68, 0.25)',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 6px rgba(239, 68, 68, 0.25)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
                        }}
                        title="Delete"
                      >
                        <i className="fas fa-trash" style={{ fontSize: '14px' }} />
                </button>
                    </div>
              </div>
            </div>
          ))}
        </div>

            {/* Pagination */}
            {news.length > itemsPerPage && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 0,
                marginTop: '30px',
                flexWrap: 'wrap'
              }}>
                <div style={{ display: 'inline-flex', gap: 0 }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '8px 10px',
                      minWidth: 36,
                      borderRadius: '6px 0 0 6px',
                      border: '1px solid #96ca2d',
                      background: currentPage === 1 ? '#f2f2f2' : '#96ca2d',
                      color: currentPage === 1 ? '#888' : '#fff',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      margin: 0,
                      borderRightWidth: 0
                    }}
                  >&lt;</button>
                  {(() => {
                    const buttons = [];
                    const renderBtn = (i, isFirst = false) => (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        style={{
                          padding: '8px 10px',
                          minWidth: 36,
                          borderRadius: 0,
                          border: `1px solid ${currentPage === i ? '#96ca2d' : '#ddd'}`,
                          background: currentPage === i ? '#96ca2d' : '#fff',
                          color: currentPage === i ? '#fff' : '#333',
                          cursor: 'pointer',
                          margin: 0,
                          borderLeftWidth: isFirst ? '1px' : 0
                        }}
                      >
                        {i}
                      </button>
                    );

                    const shown = Math.min(totalPages, 3);
                    for (let i = 1; i <= shown; i++) {
                      buttons.push(renderBtn(i, i === 1));
                    }
                    if (totalPages > 3) {
                      buttons.push(
                        <span key="ell" style={{ padding: '8px 6px', border: '1px solid #ddd', borderLeftWidth: 0 }}>...</span>
                      );
                    }
                    return buttons;
                  })()}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '8px 10px',
                      minWidth: 36,
                      borderRadius: '0 6px 6px 0',
                      border: '1px solid #96ca2d',
                      background: currentPage === totalPages ? '#f2f2f2' : '#96ca2d',
                      color: currentPage === totalPages ? '#888' : '#fff',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      margin: 0,
                      borderLeftWidth: 0
                    }}
                  >&gt;</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* News Form Modal */}
      {showNewsForm && (
        <NewsForm 
          news={editingNews}
          availableCategories={availableCategories}
          onSubmit={handleNewsSubmit}
          onCancel={() => {
            setShowNewsForm(false);
            setEditingNews(null);
          }}
        />
      )}

      {/* Popup de confirmation de suppression */}
      {newsToDelete && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: window.innerWidth <= 768 ? '10px' : '20px',
            paddingTop: window.innerWidth <= 768 ? '74px' : '20px',
            overflowY: 'auto'
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: window.innerWidth <= 768 ? '12px' : '16px',
              maxWidth: window.innerWidth <= 768 ? '100%' : '450px',
              width: '100%',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Barre de gradient */}
            <div style={{
              position: 'absolute',
              top: '0px',
              left: '0px',
              right: '0px',
              height: '4px',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              borderRadius: window.innerWidth <= 768 ? '12px 12px 0 0' : '16px 16px 0 0'
            }} />
            
            {/* Contenu de la popup */}
            <div style={{ padding: window.innerWidth <= 768 ? '16px' : '24px' }}>
              {/* Icône d'avertissement */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: window.innerWidth <= 768 ? '16px' : '20px'
              }}>
                <div style={{
                  width: window.innerWidth <= 768 ? '56px' : '64px',
                  height: window.innerWidth <= 768 ? '56px' : '64px',
                  borderRadius: '50%',
                  background: '#fef2f2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '3px solid #fee2e2'
                }}>
                  <i className="fas fa-exclamation-triangle" style={{
                    fontSize: window.innerWidth <= 768 ? '28px' : '32px',
                    color: '#ef4444'
                  }} />
                </div>
              </div>

              {/* Titre */}
              <h2 style={{
                margin: 0,
                fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                fontWeight: 700,
                color: '#1f2937',
                textAlign: 'center',
                marginBottom: '12px'
              }}>
                Delete News
              </h2>

              {/* Message */}
              <p style={{
                margin: 0,
                fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                color: '#6b7280',
                textAlign: 'center',
                lineHeight: '1.6',
                marginBottom: window.innerWidth <= 768 ? '20px' : '24px'
              }}>
                Are you sure you want to delete the news <strong>"{newsToDelete.title}"</strong>? This action is irreversible.
              </p>

              {/* Boutons */}
              <div style={{
                display: 'flex',
                gap: window.innerWidth <= 768 ? '8px' : '12px',
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
              }}>
                <button
                  onClick={() => setNewsToDelete(null)}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#e5e7eb';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f3f4f6';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteNews}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#dc2626';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#ef4444';
                  }}
                >
                  <i className="fas fa-trash" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const NewsForm = ({ news, onSubmit, onCancel, availableCategories = [] }) => {
  // Fonction pour générer un nom de fichier unique
  const generateFileName = (originalFileName, type) => {
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 9);
    const extension = originalFileName.split('.').pop();
    const prefix = type === 'image' ? 'img' : 'vid';
    return `${prefix}_${timestamp}_${randomId}.${extension}`;
  };

  // Fonction pour obtenir le chemin relatif du fichier
  const getFileRelativePath = (fileName, type) => {
    return `/aseets/images/Admin/News/${fileName}`;
  };

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image: '',
    imageType: 'link', // 'link' or 'upload'
    uploadedImage: null,
    uploadedFile: null, // Fichier original pour l'upload
    status: 'Brouillon',
    categories: [],
    media: [] // Array of { type: 'image'|'video', url: '', file: null, uploadType: 'link'|'upload' }
  });
  const [mediaError, setMediaError] = useState('');
  const [errors, setErrors] = useState({
    title: '',
    content: '',
    image: '',
    status: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [touched, setTouched] = useState({
    title: false,
    content: false,
    image: false,
    status: false
  });

  // Categories are loaded from the Categories table (see /admin/categories)

  useEffect(() => {
    if (news) {
      // Transformer les médias pour qu'ils aient les propriétés nécessaires pour l'affichage
      const transformedMedia = (news.media || []).map(mediaItem => {
        // Déterminer si c'est un upload ou un lien
        const isUpload = !mediaItem.url && (mediaItem.file_path || mediaItem.source_type === 'upload');
        // Pour les médias uploadés, utiliser l'URL transformée comme preview, mais préserver le file_path original
        // Pour les médias avec URL, utiliser l'URL directement
        const previewUrl = mediaItem.url || (mediaItem.file_path ? getFileUrl(mediaItem.file_path) : null);
        return {
          ...mediaItem,
          id: mediaItem.id || Date.now() + Math.random(), // Ajouter un ID si absent
          type: mediaItem.type || mediaItem.media_type || 'image', // Assurer que type existe
          url: mediaItem.url || previewUrl, // URL pour l'affichage (préserver l'URL originale si elle existe)
          preview: previewUrl, // Utiliser l'URL transformée comme preview pour l'affichage
          file: null, // Pas de fichier pour les médias existants
          uploadType: isUpload ? 'upload' : 'link', // Déterminer le type d'upload
          fileName: mediaItem.fileName || mediaItem.file_name || (mediaItem.file_path ? mediaItem.file_path.split('/').pop() : null),
          file_path: mediaItem.file_path || null, // Préserver le file_path original
          file_size: mediaItem.file_size || null,
          mime_type: mediaItem.mime_type || null
        };
      });
      
      setFormData({
        ...news,
        categories: news.category_ids || [],
        media: transformedMedia,
        imageType: news.image_url ? 'link' : (news.image_file_path ? 'upload' : 'link'),
        uploadedImage: news.image || null,
        uploadedFile: null // Pas de fichier pour l'image existante
      });
    }
  }, [news]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        // Stocker le fichier original pour l'upload
        // Lire le fichier en base64 pour l'aperçu
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData(prev => ({
            ...prev,
            uploadedImage: reader.result, // Base64 pour l'aperçu
            uploadedFile: file, // Fichier original pour l'upload
            image: reader.result, // Base64 pour l'aperçu
            uploadedFileName: file.name,
            uploadedFileSize: (file.size / 1024 / 1024).toFixed(2) + ' MB'
          }));
          // Réinitialiser l'erreur d'image quand un fichier est uploadé
          setErrors(prev => ({ ...prev, image: '' }));
        };
        reader.readAsDataURL(file);
      } else {
        alert('Please select a valid image file');
      }
    }
  };

  const handleAddMedia = (type) => {
    // Vérifier que tous les médias existants sont complétés
    const existingMedia = formData.media || [];
    const incompleteMedia = existingMedia.find(media => {
      // Un média est incomplet s'il n'a ni URL (pour link) ni preview/file (pour upload)
      if (media.uploadType === 'link') {
        return !media.url || media.url.trim() === '';
      } else if (media.uploadType === 'upload') {
        return !media.preview && !media.file;
      }
      return true; // Si uploadType n'est pas défini, considérer comme incomplet
    });

    if (incompleteMedia) {
      setMediaError('Please complete the current media before adding another one.');
      // Effacer l'erreur après 5 secondes
      setTimeout(() => setMediaError(''), 5000);
      return;
    }

    // Réinitialiser l'erreur si tout est OK
    setMediaError('');
    setFormData(prev => ({
      ...prev,
      media: [...(prev.media || []), {
        id: Date.now(),
        type: type,
        url: '',
        file: null,
        uploadType: 'link',
        preview: null
      }]
    }));
  };

  const handleMediaChange = (index, field, value) => {
    // Réinitialiser l'erreur quand l'utilisateur modifie un média
    if (mediaError) {
      setMediaError('');
    }
    setFormData(prev => {
      const updatedMedia = [...(prev.media || [])];
      updatedMedia[index] = {
        ...updatedMedia[index],
        [field]: value
      };
      return {
        ...prev,
        media: updatedMedia
      };
    });
  };

  const handleMediaUpload = async (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const mediaItem = formData.media[index];
      if (mediaItem.type === 'image' && file.type.startsWith('image/')) {
        // Générer un nom de fichier unique
        const fileName = generateFileName(file.name, 'image');
        const relativePath = getFileRelativePath(fileName, 'image');
        
        const reader = new FileReader();
        reader.onloadend = () => {
          handleMediaChange(index, 'file', file);
          handleMediaChange(index, 'url', reader.result); // Base64 pour l'aperçu
          handleMediaChange(index, 'preview', reader.result);
          handleMediaChange(index, 'uploadType', 'upload');
          handleMediaChange(index, 'fileName', fileName); // Nom du fichier qui sera sauvegardé
          handleMediaChange(index, 'filePath', relativePath); // Chemin relatif
          handleMediaChange(index, 'fileSize', (file.size / 1024 / 1024).toFixed(2) + ' MB');
          // Réinitialiser l'erreur quand un fichier est uploadé
          setMediaError('');
        };
        reader.readAsDataURL(file);
        
        // Si vous avez un backend, décommentez ceci pour sauvegarder le fichier:
        /*
        try {
          const savedPath = await saveFileToServer(file, fileName, 'image');
          handleMediaChange(index, 'url', savedPath);
          handleMediaChange(index, 'fileName', fileName);
          handleMediaChange(index, 'filePath', savedPath);
        } catch (error) {
          alert('Error uploading image');
        }
        */
      } else if (mediaItem.type === 'video' && file.type.startsWith('video/')) {
        // Générer un nom de fichier unique
        const fileName = generateFileName(file.name, 'video');
        const relativePath = getFileRelativePath(fileName, 'video');
        
        const reader = new FileReader();
        reader.onloadend = () => {
          handleMediaChange(index, 'file', file);
          handleMediaChange(index, 'url', reader.result); // Base64 pour l'aperçu
          handleMediaChange(index, 'preview', reader.result);
          handleMediaChange(index, 'uploadType', 'upload');
          handleMediaChange(index, 'fileName', fileName); // Nom du fichier qui sera sauvegardé
          handleMediaChange(index, 'filePath', relativePath); // Chemin relatif
          handleMediaChange(index, 'fileSize', (file.size / 1024 / 1024).toFixed(2) + ' MB');
          // Réinitialiser l'erreur quand un fichier est uploadé
          setMediaError('');
        };
        reader.readAsDataURL(file);
        
        // Si vous avez un backend, décommentez ceci pour sauvegarder le fichier:
        /*
        try {
          const savedPath = await saveFileToServer(file, fileName, 'video');
          handleMediaChange(index, 'url', savedPath);
          handleMediaChange(index, 'fileName', fileName);
          handleMediaChange(index, 'filePath', savedPath);
        } catch (error) {
          alert('Error uploading video');
        }
        */
      } else {
        alert(`Please select a valid ${mediaItem.type === 'image' ? 'image' : 'video'} file`);
      }
    }
  };

  const handleRemoveMedia = (index) => {
    setFormData(prev => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index)
    }));
  };

  const handleCategoryChange = (categoryId) => {
    setFormData(prev => {
      const currentCategories = prev.categories || [];
      if (currentCategories.includes(categoryId)) {
        return {
          ...prev,
          categories: currentCategories.filter(c => c !== categoryId)
        };
      } else {
        return {
          ...prev,
          categories: [...currentCategories, categoryId]
        };
      }
    });
  };

  const validateForm = () => {
    const newErrors = {
      title: '',
      content: '',
      image: '',
      status: ''
    };
    let isValid = true;

    // Valider le titre
    if (!formData.title || formData.title.trim() === '') {
      newErrors.title = 'Title is required';
      isValid = false;
    }

    // Valider le contenu
    // Remove HTML tags to check if content is empty
    const textContent = formData.content ? formData.content.replace(/<[^>]*>/g, '').trim() : '';
    if (!textContent || textContent === '') {
      newErrors.content = 'Content is required';
      isValid = false;
    }

    // Valider le statut
    if (!formData.status || formData.status.trim() === '') {
      newErrors.status = 'Status is required';
      isValid = false;
    }

    // Valider l'image principale
    if (formData.imageType === 'link') {
      if (!formData.image || formData.image.trim() === '') {
        newErrors.image = 'Main image is required';
        isValid = false;
      }
    } else if (formData.imageType === 'upload') {
      if (!formData.uploadedImage && !formData.image) {
        newErrors.image = 'Main image is required';
        isValid = false;
      }
    } else {
      // Si aucun type n'est défini ou si l'image est vide
      if (!formData.image || formData.image.trim() === '') {
        newErrors.image = 'Main image is required';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Marquer comme soumis AVANT la validation pour afficher les erreurs
    setIsSubmitted(true);
    
    // Valider le formulaire (cela va mettre à jour les erreurs)
    const isValid = validateForm();
    
    if (isValid) {
      onSubmit(formData);
      setIsSubmitted(false);
      // Réinitialiser les erreurs après soumission réussie
      setErrors({
        title: '',
        content: '',
        image: '',
        status: ''
      });
    }
  };

  return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: window.innerWidth <= 768 ? '10px' : '20px',
          paddingTop: window.innerWidth <= 768 ? '74px' : '20px',
          animation: 'fadeIn 0.2s ease-in',
          overflowY: 'auto'
        }}
      >
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @media (max-width: 768px) {
          .news-form-container {
            max-width: 100% !important;
            border-radius: 12px !important;
            max-height: 95vh !important;
          }
          .news-form-header {
            padding: 16px !important;
            border-radius: 12px 12px 0 0 !important;
          }
          .news-form-content {
            padding: 20px 16px !important;
          }
        }
      `}</style>
      <div
        className="news-form-container"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: window.innerWidth <= 768 ? '12px' : '20px',
          maxWidth: window.innerWidth <= 768 ? '100%' : '900px',
          width: '100%',
          maxHeight: window.innerWidth <= 768 ? '95vh' : '90vh',
          overflowY: 'auto',
          position: 'relative',
          boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4)',
          animation: 'slideUp 0.3s ease-out'
        }}
      >
        {/* Header avec gradient */}
        <div 
          className="news-form-header"
          style={{
            background: 'linear-gradient(135deg, rgb(150, 202, 45) 0%, rgb(127, 184, 0) 100%)',
            padding: window.innerWidth <= 768 ? '16px' : '24px 24px 20px 24px',
            borderRadius: window.innerWidth <= 768 ? '12px 12px 0 0' : '20px 20px 0 0',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
          }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ 
              margin: 0,
              fontSize: window.innerWidth <= 768 ? '18px' : '24px',
              fontWeight: 700,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <i className={`fas ${news ? 'fa-edit' : 'fa-plus-circle'}`} style={{ fontSize: window.innerWidth <= 768 ? '16px' : '22px' }} />
              {news ? 'Edit' : 'New'} News
            </h2>
        <button
          onClick={onCancel}
          style={{
                width: window.innerWidth <= 768 ? '32px' : '36px',
                height: window.innerWidth <= 768 ? '32px' : '36px',
            borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
                color: 'white',
            fontSize: window.innerWidth <= 768 ? '16px' : '18px',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)'
          }}
          onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                e.currentTarget.style.transform = 'rotate(90deg)';
          }}
          onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'rotate(0deg)';
          }}
        >
          <i className="fas fa-times" />
        </button>
          </div>
        </div>

          {/* Contenu du formulaire */}
          <div className="news-form-content" style={{ padding: window.innerWidth <= 768 ? '20px 16px' : '32px' }}>
        <form onSubmit={handleSubmit}>
          {/* Titre */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: '8px',
              marginBottom: '10px', 
              fontWeight: 600,
              color: '#1f2937',
              fontSize: '14px'
            }}>
              <i className="fas fa-heading" style={{ color: '#96ca2d', fontSize: '14px' }} />
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => {
                const newValue = e.target.value;
                setFormData({ ...formData, title: newValue });
                // Réinitialiser l'erreur si le champ est maintenant rempli
                if (newValue.trim() !== '' && errors.title) {
                  setErrors(prev => ({ ...prev, title: '' }));
                }
              }}
              placeholder="Enter the news title"
              style={{ 
                width: '100%', 
                padding: '12px 16px', 
                border: (isSubmitted && errors.title) ? '2px solid #ef4444' : '2px solid #e5e7eb', 
                borderRadius: '10px',
                fontSize: '15px',
                transition: 'all 0.3s ease',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#96ca2d';
                e.target.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
              }}
              onBlur={(e) => {
                if (!isSubmitted || !errors.title) {
                  e.target.style.borderColor = '#e5e7eb';
                }
                e.target.style.boxShadow = 'none';
              }}
            />
            {isSubmitted && errors.title ? (
              <div style={{
                marginTop: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <i className="fas fa-exclamation-circle" style={{ 
                  color: '#ef4444', 
                  fontSize: '12px',
                  flexShrink: 0
                }} />
                <span style={{ 
                  color: '#dc2626', 
                  fontSize: '12px',
                  fontWeight: 500
                }}>
                  {errors.title}
                </span>
              </div>
            ) : null}
          </div>

          {/* Contenu */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: '8px',
              marginBottom: '10px', 
              fontWeight: 600,
              color: '#1f2937',
              fontSize: '14px'
            }}>
              <i className="fas fa-file-alt" style={{ color: '#96ca2d', fontSize: '14px' }} />
              Content *
            </label>
            <RichTextEditor
              value={formData.content}
              onChange={(value) => {
                // Remove HTML tags to check if content is empty
                const textContent = value.replace(/<[^>]*>/g, '').trim();
                setFormData({ ...formData, content: value });
                // Réinitialiser l'erreur si le champ est maintenant rempli
                if (textContent !== '' && errors.content) {
                  setErrors({ ...errors, content: '' });
                }
              }}
              placeholder="Enter the full news content"
              style={{
                border: (isSubmitted && errors.content) ? '2px solid #ef4444' : undefined
              }}
            />
            {isSubmitted && errors.content && (
              <div style={{
                marginTop: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <i className="fas fa-exclamation-circle" style={{ 
                  color: '#ef4444', 
                  fontSize: '12px',
                  flexShrink: 0
                }} />
                <span style={{ 
                  color: '#dc2626', 
                  fontSize: '12px',
                  fontWeight: 500
                }}>
                  {errors.content}
                </span>
              </div>
            )}
          </div>

          {/* Image principale */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px', 
              fontWeight: 600,
              color: '#1f2937',
              fontSize: '14px'
            }}>
              <i className="fas fa-image" style={{ color: '#96ca2d', fontSize: '14px' }} />
              Main Image *
            </label>
            <div style={{ 
              padding: '16px',
              border: (isSubmitted && errors.image) ? '2px dashed #ef4444' : '2px dashed #e5e7eb',
              borderRadius: '12px',
              background: '#f9fafb',
              transition: 'all 0.3s ease'
            }}>
              <div style={{ 
                display: 'flex', 
                gap: window.innerWidth <= 768 ? '8px' : '10px', 
                marginBottom: '16px',
                marginRight: '0',
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
              }}>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, imageType: 'link' })}
                  style={{
                    flex: 1,
                    padding: window.innerWidth <= 768 ? '12px 16px' : '12px 20px',
                    background: formData.imageType === 'link' 
                      ? 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)' 
                      : '#ffffff',
                    color: formData.imageType === 'link' ? 'white' : '#374151',
                    border: formData.imageType === 'link' ? 'none' : '2px solid #e5e7eb',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    boxShadow: formData.imageType === 'link' ? '0 4px 12px rgba(150, 202, 45, 0.3)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (formData.imageType !== 'link') {
                      e.currentTarget.style.borderColor = '#96ca2d';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (formData.imageType !== 'link') {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <i className="fas fa-link" />
                  URL Link
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, imageType: 'upload' })}
                  style={{
                    flex: 1,
                    padding: window.innerWidth <= 768 ? '12px 16px' : '12px 20px',
                    background: formData.imageType === 'upload' 
                      ? 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)' 
                      : '#ffffff',
                    color: formData.imageType === 'upload' ? 'white' : '#374151',
                    border: formData.imageType === 'upload' ? 'none' : '2px solid #e5e7eb',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    boxShadow: formData.imageType === 'upload' ? '0 4px 12px rgba(150, 202, 45, 0.3)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (formData.imageType !== 'upload') {
                      e.currentTarget.style.borderColor = '#96ca2d';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (formData.imageType !== 'upload') {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <i className="fas fa-cloud-upload-alt" />
                  Upload fichier
                </button>
              </div>
              {formData.imageType === 'link' ? (
            <input
              type="text"
              value={formData.image}
              onChange={(e) => {
                const newValue = e.target.value;
                setFormData({ ...formData, image: newValue });
                // Réinitialiser l'erreur si le champ est maintenant rempli
                if (newValue.trim() !== '' && errors.image) {
                  setErrors({ ...errors, image: '' });
                }
              }}
                  placeholder="https://exemple.com/image.jpg"
                  style={{ 
                    width: '100%', 
                    padding: '12px 16px', 
                    border: (isSubmitted && errors.image) ? '2px solid #ef4444' : '2px solid #e5e7eb', 
                    borderRadius: '10px',
                    fontSize: '14px',
                    transition: 'all 0.3s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#96ca2d';
                    e.target.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
                  }}
                  onBlur={(e) => {
                    if (!isSubmitted || !errors.image) {
                      e.target.style.borderColor = '#e5e7eb';
                    }
                    e.target.style.boxShadow = 'none';
                  }}
                />
              ) : (
                <div>
                  <label 
                    htmlFor="image-upload"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '4px 8px',
                      border: '3px dashed #96ca2d',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, rgba(150, 202, 45, 0.08) 0%, rgba(150, 202, 45, 0.03) 100%)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(150, 202, 45, 0.15) 0%, rgba(150, 202, 45, 0.08) 100%)';
                      e.currentTarget.style.borderColor = '#7ba818';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(150, 202, 45, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(150, 202, 45, 0.08) 0%, rgba(150, 202, 45, 0.03) 100%)';
                      e.currentTarget.style.borderColor = '#96ca2d';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                      id="image-upload"
                    />
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '4px',
                      boxShadow: '0 2px 6px rgba(150, 202, 45, 0.3)'
                    }}>
                      <i className="fas fa-cloud-upload-alt" style={{ fontSize: '14px', color: 'white' }} />
          </div>
                    <span style={{ 
                      color: '#1f2937', 
                      fontWeight: 600, 
                      fontSize: '11px',
                      marginBottom: '2px'
                    }}>
                      Click to select an image
                    </span>
                    <span style={{ 
                      color: '#6b7280', 
                      fontSize: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px'
                    }}>
                      <i className="fas fa-info-circle" style={{ fontSize: '8px' }} />
                      PNG, JPG, GIF up to 50MB
                    </span>
                  </label>
                  {formData.imageType === 'upload' && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      background: '#fef3c7',
                      border: '1px solid #fbbf24',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#92400e',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <i className="fas fa-exclamation-triangle" style={{ fontSize: '12px' }} />
                      <span>The image file size must not exceed 50MB</span>
                    </div>
                  )}
                  {formData.uploadedImage && (
                    <div style={{ marginTop: '16px' }}>
                      <div style={{
                        padding: '12px 16px',
                        background: 'linear-gradient(135deg, rgba(150, 202, 45, 0.1) 0%, rgba(150, 202, 45, 0.05) 100%)',
                        borderRadius: '10px',
                        border: '2px solid #96ca2d',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <i className="fas fa-check" style={{ color: 'white', fontSize: '14px' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            fontWeight: 600, 
                            color: '#1f2937', 
                            fontSize: '14px',
                            marginBottom: '2px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {formData.uploadedFileName || 'Selected file'}
                          </div>
                          {formData.uploadedFileSize && (
                            <div style={{ 
                              color: '#6b7280', 
                              fontSize: '12px' 
                            }}>
                              {formData.uploadedFileSize}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <img 
                          src={formData.uploadedImage} 
                          alt="Preview" 
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '250px', 
                            borderRadius: '12px', 
                            border: '2px solid #e5e7eb',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
              {formData.image && formData.imageType === 'link' && (
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <img 
                    src={formData.image} 
                    alt="Preview" 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '250px', 
                      borderRadius: '12px', 
                      border: '2px solid #e5e7eb',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>
            {isSubmitted && errors.image && (
              <div style={{
                marginTop: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <i className="fas fa-exclamation-circle" style={{ 
                  color: '#ef4444', 
                  fontSize: '12px',
                  flexShrink: 0
                }} />
                <span style={{ 
                  color: '#dc2626', 
                  fontSize: '12px',
                  fontWeight: 500
                }}>
                  {errors.image}
                </span>
              </div>
            )}
          </div>

          {/* Section Médias */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: '2px solid #e5e7eb'
            }}>
              <label style={{ 
                fontWeight: 600,
                color: '#1f2937',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <i className="fas fa-photo-video" style={{ color: '#96ca2d', fontSize: '14px' }} />
                Media (Images/Videos)
              </label>
              <div style={{ 
                display: 'flex', 
                gap: window.innerWidth <= 768 ? '8px' : '10px',
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setMediaError('');
                    handleAddMedia('image');
                  }}
                  style={{
                    padding: window.innerWidth <= 768 ? '10px 16px' : '10px 18px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: window.innerWidth <= 768 ? '12px' : '13px',
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    flex: window.innerWidth <= 768 ? '1' : 'none'
                  }}
                  onMouseEnter={(e) => { 
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                  }}
                  onMouseLeave={(e) => { 
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
                  }}
                >
                  <i className="fas fa-image" />
                  Image
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMediaError('');
                    handleAddMedia('video');
                  }}
                  style={{
                    padding: window.innerWidth <= 768 ? '10px 16px' : '10px 18px',
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: window.innerWidth <= 768 ? '12px' : '13px',
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    flex: window.innerWidth <= 768 ? '1' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
                  }}
                  onMouseEnter={(e) => { 
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
                  }}
                  onMouseLeave={(e) => { 
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.3)';
                  }}
                >
                  <i className="fas fa-video" />
                  Video
                </button>
              </div>
            </div>
            {mediaError && (
              <div style={{
                marginTop: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <i className="fas fa-exclamation-circle" style={{ 
                  color: '#ef4444', 
                  fontSize: '14px',
                  flexShrink: 0
                }} />
                <span style={{ 
                  color: '#dc2626', 
                  fontSize: '14px',
                  fontWeight: 500
                }}>
                  {mediaError}
                </span>
              </div>
            )}
            {formData.media && formData.media.length > 0 && (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '16px'
              }}>
                {formData.media.map((mediaItem, index) => (
                  <div key={mediaItem.id || index} style={{
                    padding: '20px',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                    borderRadius: '12px',
                    border: '2px solid #e5e7eb',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#96ca2d';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(150, 202, 45, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                  }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ 
                        padding: '4px 8px', 
                        background: mediaItem.type === 'image' ? '#3b82f6' : '#ef4444',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        {mediaItem.type === 'image' ? 'Image' : 'Video'}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveMedia(index)}
                        style={{
                          padding: '4px 8px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        <i className="fas fa-times" />
                      </button>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      gap: window.innerWidth <= 768 ? '8px' : '10px', 
                      marginBottom: '12px',
                      flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
                    }}>
                      <button
                        type="button"
                        onClick={() => handleMediaChange(index, 'uploadType', 'link')}
                        style={{
                          flex: 1,
                          padding: window.innerWidth <= 768 ? '10px 14px' : '10px 16px',
                          background: mediaItem.uploadType === 'link' 
                            ? 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)' 
                            : '#ffffff',
                          color: mediaItem.uploadType === 'link' ? 'white' : '#374151',
                          border: mediaItem.uploadType === 'link' ? 'none' : '2px solid #e5e7eb',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: window.innerWidth <= 768 ? '12px' : '13px',
                          fontWeight: 600,
                          transition: 'all 0.3s ease',
                          boxShadow: mediaItem.uploadType === 'link' ? '0 2px 8px rgba(150, 202, 45, 0.3)' : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                        onMouseEnter={(e) => {
                          if (mediaItem.uploadType !== 'link') {
                            e.currentTarget.style.borderColor = '#96ca2d';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (mediaItem.uploadType !== 'link') {
                            e.currentTarget.style.borderColor = '#e5e7eb';
                          }
                        }}
                      >
                        <i className="fas fa-link" />
                        Link
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMediaChange(index, 'uploadType', 'upload')}
                        style={{
                          flex: 1,
                          padding: window.innerWidth <= 768 ? '10px 14px' : '10px 16px',
                          background: mediaItem.uploadType === 'upload' 
                            ? 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)' 
                            : '#ffffff',
                          color: mediaItem.uploadType === 'upload' ? 'white' : '#374151',
                          border: mediaItem.uploadType === 'upload' ? 'none' : '2px solid #e5e7eb',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: window.innerWidth <= 768 ? '12px' : '13px',
                          fontWeight: 600,
                          transition: 'all 0.3s ease',
                          boxShadow: mediaItem.uploadType === 'upload' ? '0 2px 8px rgba(150, 202, 45, 0.3)' : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                        onMouseEnter={(e) => {
                          if (mediaItem.uploadType !== 'upload') {
                            e.currentTarget.style.borderColor = '#96ca2d';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (mediaItem.uploadType !== 'upload') {
                            e.currentTarget.style.borderColor = '#e5e7eb';
                          }
                        }}
                      >
                        <i className="fas fa-cloud-upload-alt" />
                        Upload
                      </button>
                    </div>
                    {mediaItem.uploadType === 'link' ? (
                      <input
                        type="text"
                        value={mediaItem.url || ''}
                        onChange={(e) => handleMediaChange(index, 'url', e.target.value)}
                        placeholder={mediaItem.type === 'image' ? 'https://exemple.com/image.jpg' : 'https://exemple.com/video.mp4'}
                        style={{ 
                          width: '100%', 
                          padding: window.innerWidth <= 768 ? '10px 14px' : '12px 16px', 
                          border: '2px solid #e5e7eb', 
                          borderRadius: '10px', 
                          fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                          transition: 'all 0.3s ease',
                          outline: 'none'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#96ca2d';
                          e.target.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    ) : (
                      <div>
                        <label
                          htmlFor={`media-upload-${index}`}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: window.innerWidth <= 768 ? '8px 12px' : '4px 8px',
                            border: window.innerWidth <= 768 ? '2px dashed #96ca2d' : '3px dashed #96ca2d',
                            borderRadius: window.innerWidth <= 768 ? '10px' : '12px',
                            background: 'linear-gradient(135deg, rgba(150, 202, 45, 0.08) 0%, rgba(150, 202, 45, 0.03) 100%)',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            position: 'relative',
                            overflow: 'hidden',
                            minHeight: window.innerWidth <= 768 ? '60px' : 'auto'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(150, 202, 45, 0.15) 0%, rgba(150, 202, 45, 0.08) 100%)';
                            e.currentTarget.style.borderColor = '#7ba818';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 8px 20px rgba(150, 202, 45, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(150, 202, 45, 0.08) 0%, rgba(150, 202, 45, 0.03) 100%)';
                            e.currentTarget.style.borderColor = '#96ca2d';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <input
                            type="file"
                            accept={mediaItem.type === 'image' ? 'image/*' : 'video/*'}
                            onChange={(e) => handleMediaUpload(index, e)}
                            style={{ display: 'none' }}
                            id={`media-upload-${index}`}
                          />
                          <div style={{
                            width: window.innerWidth <= 768 ? '28px' : '32px',
                            height: window.innerWidth <= 768 ? '28px' : '32px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: window.innerWidth <= 768 ? '2px' : '4px',
                            boxShadow: '0 2px 6px rgba(150, 202, 45, 0.3)'
                          }}>
                            <i className="fas fa-cloud-upload-alt" style={{ fontSize: window.innerWidth <= 768 ? '12px' : '14px', color: 'white' }} />
              </div>
                              <span style={{ 
                                color: '#1f2937', 
                                fontWeight: 600, 
                                fontSize: window.innerWidth <= 768 ? '10px' : '11px',
                                marginBottom: window.innerWidth <= 768 ? '1px' : '2px'
                              }}>
                                Click to select a {mediaItem.type === 'image' ? 'image file' : 'video file'}
                              </span>
                          <span style={{ 
                            color: '#6b7280', 
                            fontSize: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '3px'
                          }}>
                            <i className="fas fa-info-circle" style={{ fontSize: '8px' }} />
                            {mediaItem.type === 'image' ? 'PNG, JPG, GIF' : 'MP4, MOV, AVI'} up to 50MB
                          </span>
                        </label>
                        {mediaItem.uploadType === 'upload' && (
                          <div style={{
                            marginTop: '8px',
                            padding: '8px 12px',
                            background: '#fef3c7',
                            border: '1px solid #fbbf24',
                            borderRadius: '6px',
                            fontSize: '12px',
                            color: '#92400e',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <i className="fas fa-exclamation-triangle" style={{ fontSize: '12px' }} />
                            <span>The {mediaItem.type === 'image' ? 'image' : 'video'} file size must not exceed 50MB</span>
                          </div>
                        )}
                      </div>
                    )}
                    {(mediaItem.preview || mediaItem.url || (mediaItem.uploadType === 'upload' && mediaItem.file_path)) && (
                      <div style={{ marginTop: '16px' }}>
                        {mediaItem.uploadType === 'upload' && mediaItem.fileName && (
                          <div style={{
                            padding: '10px 14px',
                            background: mediaItem.type === 'image'
                              ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)'
                              : 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)',
                            borderRadius: '8px',
                            border: `2px solid ${mediaItem.type === 'image' ? '#3b82f6' : '#ef4444'}`,
                            marginBottom: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                          }}>
                            <div style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              background: mediaItem.type === 'image'
                                ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                                : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <i className="fas fa-check" style={{ color: 'white', fontSize: '12px' }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ 
                                fontWeight: 600, 
                                color: '#1f2937', 
                                fontSize: '13px',
                                marginBottom: '2px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {mediaItem.fileName}
                              </div>
                              {mediaItem.fileSize && (
                                <div style={{ 
                                  color: '#6b7280', 
                                  fontSize: '11px' 
                                }}>
                                  {mediaItem.fileSize}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        <div style={{ textAlign: 'center' }}>
                          {mediaItem.type === 'image' ? (
                            <img 
                              src={mediaItem.preview || mediaItem.url || (mediaItem.file_path ? getFileUrl(mediaItem.file_path) : '')} 
                              alt="Preview" 
                              style={{ 
                                maxWidth: '100%', 
                                maxHeight: '200px', 
                                borderRadius: '12px', 
                                border: '2px solid #e5e7eb',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                              }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            (() => {
                              const videoUrl = mediaItem.preview || mediaItem.url || (mediaItem.file_path ? getFileUrl(mediaItem.file_path) : '');
                              const isYouTube = videoUrl && (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be'));
                              
                              return isYouTube ? (
                                <iframe
                                  src={convertYouTubeUrl(videoUrl)}
                                  width="100%"
                                  height="200"
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  style={{ 
                                    borderRadius: '12px', 
                                    border: '2px solid #e5e7eb',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                  }}
                                />
                              ) : (
                                <video 
                                  src={videoUrl} 
                                  controls
                                  style={{ 
                                    maxWidth: '100%', 
                                    maxHeight: '200px', 
                                    borderRadius: '12px', 
                                    border: '2px solid #e5e7eb',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                  }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              );
                            })()
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Catégories */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px', 
              fontWeight: 600,
              color: '#1f2937',
              fontSize: '14px'
            }}>
              <i className="fas fa-tags" style={{ color: '#96ca2d', fontSize: '14px' }} />
              Categories
            </label>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(auto-fill, minmax(120px, 1fr))' : 'repeat(auto-fill, minmax(160px, 1fr))', 
              gap: window.innerWidth <= 768 ? '8px' : '10px',
              padding: window.innerWidth <= 768 ? '12px' : '16px',
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
              transition: 'all 0.3s ease'
            }}>
              {availableCategories.map((category) => (
                <label 
                  key={category.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    background: formData.categories && formData.categories.includes(category.id) 
                      ? 'linear-gradient(135deg, rgba(150, 202, 45, 0.15) 0%, rgba(150, 202, 45, 0.1) 100%)' 
                      : '#ffffff',
                    border: formData.categories && formData.categories.includes(category.id)
                      ? '2px solid #96ca2d'
                      : '2px solid #e5e7eb',
                    transition: 'all 0.3s ease',
                    boxShadow: formData.categories && formData.categories.includes(category.id)
                      ? '0 2px 8px rgba(150, 202, 45, 0.2)'
                      : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (!formData.categories || !formData.categories.includes(category.id)) {
                      e.currentTarget.style.background = '#f9fafb';
                      e.currentTarget.style.borderColor = '#96ca2d';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!formData.categories || !formData.categories.includes(category.id)) {
                      e.currentTarget.style.background = '#ffffff';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.categories && formData.categories.includes(category.id)}
                    onChange={() => handleCategoryChange(category.id)}
                    style={{ 
                      cursor: 'pointer',
                      width: '18px',
                      height: '18px',
                      accentColor: '#96ca2d'
                    }}
                  />
                  <span style={{ 
                    fontSize: '14px', 
                    color: formData.categories && formData.categories.includes(category.id) ? '#1f2937' : '#374151',
                    fontWeight: formData.categories && formData.categories.includes(category.id) ? 600 : 500
                  }}>
                    {category.name}
                  </span>
                </label>
              ))}
            </div>
            {formData.categories && formData.categories.length > 0 && (
              <div style={{ 
                marginTop: '12px', 
                padding: '10px 16px',
                background: 'rgba(150, 202, 45, 0.1)',
                borderRadius: '8px',
                fontSize: '13px', 
                color: '#1f2937',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <i className="fas fa-check-circle" style={{ color: '#96ca2d' }} />
                {formData.categories.length} catégorie{formData.categories.length > 1 ? 's' : ''} sélectionnée{formData.categories.length > 1 ? 's' : ''}
              </div>
            )}
          </div>

          {/* Status (after Categories) */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: '8px',
              marginBottom: '10px', 
              fontWeight: 600,
              color: '#1f2937',
              fontSize: '14px'
            }}>
              <i className="fas fa-toggle-on" style={{ color: '#96ca2d', fontSize: '14px' }} />
              Status
            </label>
            <div style={{ 
              position: 'relative',
              background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
              borderRadius: '12px',
              border: (isSubmitted && errors.status) ? '2px solid #ef4444' : '2px solid #e5e7eb',
              transition: 'all 0.3s ease',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              const selectElement = e.currentTarget.querySelector('select');
              if (document.activeElement !== selectElement) {
                e.currentTarget.style.borderColor = '#96ca2d';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(150, 202, 45, 0.1)';
              }
            }}
            onMouseLeave={(e) => {
              const selectElement = e.currentTarget.querySelector('select');
              if (document.activeElement !== selectElement) {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
            >
            <select
              value={formData.status}
              onChange={(e) => {
                setFormData({ ...formData, status: e.target.value });
                if (errors.status) {
                  setErrors({ ...errors, status: '' });
                }
              }}
                style={{ 
                  width: '100%', 
                  padding: '10px 45px 10px 14px', 
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  background: 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  outline: 'none',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  fontWeight: 500,
                  color: '#1f2937',
                  lineHeight: '1.5'
                }}
                onFocus={(e) => {
                  const container = e.target.parentElement;
                  container.style.borderColor = '#96ca2d';
                  container.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.15)';
                  container.style.background = 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)';
                  const arrow = container.querySelector('.select-arrow');
                  if (arrow) arrow.style.transform = 'translateY(-50%) rotate(180deg)';
                }}
                onBlur={(e) => {
                  const container = e.target.parentElement;
                  if (!isSubmitted || !errors.status) {
                    container.style.borderColor = '#e5e7eb';
                  }
                  container.style.boxShadow = 'none';
                  container.style.background = 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)';
                  const arrow = container.querySelector('.select-arrow');
                  if (arrow) arrow.style.transform = 'translateY(-50%) rotate(0deg)';
                }}
                onMouseDown={(e) => {
                  const container = e.target.parentElement;
                  const arrow = container.querySelector('.select-arrow');
                  if (arrow) arrow.style.transform = 'translateY(-50%) rotate(180deg)';
                }}
              >
                <option value="Brouillon" style={{ padding: '10px', background: '#ffffff' }}>📝 Draft</option>
                <option value="Publié" style={{ padding: '10px', background: '#ffffff' }}>✅ Published</option>
            </select>
              <div 
                className="select-arrow"
                style={{
                  position: 'absolute',
                  right: '18px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#96ca2d',
                  fontSize: '16px',
                  transition: 'transform 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'rgba(150, 202, 45, 0.1)'
                }}
              >
                <i className="fas fa-chevron-down" />
          </div>
            </div>
            {isSubmitted && errors.status && (
            <div style={{ 
                marginTop: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <i className="fas fa-exclamation-circle" style={{ 
                  color: '#ef4444', 
                  fontSize: '12px',
                  flexShrink: 0
                }} />
                <span style={{ 
                  color: '#dc2626', 
                  fontSize: '12px',
                  fontWeight: 500
                }}>
                  {errors.status}
                </span>
              </div>
            )}
          </div>

          {/* Boutons d'action */}
          <div style={{ 
            display: 'flex', 
            gap: window.innerWidth <= 768 ? '8px' : '12px', 
            justifyContent: window.innerWidth <= 768 ? 'stretch' : 'flex-end', 
            marginTop: window.innerWidth <= 768 ? '24px' : '32px',
            paddingTop: window.innerWidth <= 768 ? '20px' : '24px',
            borderTop: '2px solid #e5e7eb',
            flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
          }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: window.innerWidth <= 768 ? '12px 20px' : '14px 28px',
                background: '#ffffff',
                color: '#374151',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                fontWeight: 600,
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                flex: window.innerWidth <= 768 ? '1' : 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f9fafb';
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <i className="fas fa-times" />
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: window.innerWidth <= 768 ? '12px 20px' : '14px 28px',
                background: 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                fontWeight: 600,
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(150, 202, 45, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                flex: window.innerWidth <= 768 ? '1' : 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(150, 202, 45, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(150, 202, 45, 0.3)';
              }}
            >
              <i className={`fas ${news ? 'fa-save' : 'fa-plus-circle'}`} />
              {news ? 'Update' : 'Create'} News
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default News;


